import React from 'react'
import { Controller } from 'react-hook-form'

import { cn } from 'src/shared/utils/cn'

type AuthCodeInputProps = {
  control: any
  name: string
  disabled?: boolean
  className?: string
  inputClassName?: string
  isConsumerLogin?: boolean
}

function formatWithDash(val: string, isConsumerLogin: boolean) {
  const maxLength = isConsumerLogin ? 8 : 6
  const dashIndex = isConsumerLogin ? 4 : 3

  // Only digits, max 6
  const digits = val.replace(/[^0-9]/g, '').slice(0, maxLength)
  if (digits.length <= dashIndex) return digits
  return digits.slice(0, dashIndex) + '-' + digits.slice(dashIndex)
}

function stripDash(val: string, maxLength = 6) {
  return val.replace(/[^0-9]/g, '').slice(0, maxLength)
}

const AuthCodeInput: React.FC<AuthCodeInputProps> = ({
  control,
  name,
  disabled,
  className,
  inputClassName,
  isConsumerLogin = false,
}) => (
  <Controller
    control={control}
    name={name}
    rules={{
      required: true,
      pattern: isConsumerLogin ? /^[0-9]{8}$/ : /^[0-9]{6}$/,
    }}
    render={({ field: { value = '', onChange, onBlur, ref }, fieldState: { error } }) => (
      <div className={cn('flex w-full flex-col items-center', className)}>
        <input
          ref={ref}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={isConsumerLogin ? 9 : 7} // 6 digits + 1 dash
          value={formatWithDash(value, isConsumerLogin)}
          onChange={(e) => {
            const raw = stripDash(e.target.value, isConsumerLogin ? 8 : 6)
            onChange(raw)
          }}
          onBlur={onBlur}
          disabled={disabled}
          className={cn(
            `
            mt-4 w-full max-w-md rounded-md border border-border bg-white
            px-3.5 py-3 text-center text-2xl tracking-[0.5em]
            focus:border-black focus:outline-none focus-visible:border-2
            ${error ? 'border-red-400' : ''}
          `,
            inputClassName,
          )}
          placeholder={isConsumerLogin ? '____ ____' : '___ ___'}
          style={{
            letterSpacing: '0.5em',
            fontVariantNumeric: 'tabular-nums',
          }}
        />
        {error && (
          <span className="mt-2 text-xs text-red-500">
            Please enter a valid {isConsumerLogin ? '8 digit' : '6 digit'} code
          </span>
        )}
      </div>
    )}
  />
)

export default AuthCodeInput
